// THPRT-6415: Stargate affiliate attribution capture (ADDITIVE).
//
// Captures the values Stargate appends on a referred landing (sg_click token,
// ref, sub1..5, utm_*) and persists them first-touch until the application is
// created. It reads and alters nothing in the legacy afsAid / ibc / utm flow.
//
// Persistence backend is switchable via SG_STORAGE_MODE below:
//   'cookie'       - a first-party cookie with a native 30-day max-age (the
//                    browser expires it).
//   'localStorage' - localStorage plus a capturedAt stamp; the 30-day TTL is
//                    enforced in code (localStorage has no native expiry).
// Both hold the same JSON bundle, honour first-touch (never overwrite a still
// fresh stored token), and expose the same read/capture API.

import * as Enums from './enums'

export type SgStorageMode = 'cookie' | 'localStorage'

// Toggle the persistence backend here. Asserted to the union (not the literal)
// so both branches below stay live for the switch.
export const SG_STORAGE_MODE = 'cookie' as SgStorageMode

// localStorage key and cookie name for OUR store (distinct from the inbound
// sg_click token cookie that the resolver sets).
const SG_STORE_KEY = Enums.__STORAGE.sgAttribution
const SG_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000

export interface SgAttribution {
    token: string
    ref?: string
    subIds?: Record<string, string>
    utm?: Record<string, string>
    capturedAt: string
}

// Read a cookie value by name, decoded exactly once. Used for both OUR store
// (cookie mode) and the inbound sg_click token cookie fallback.
const readCookie = (name: string): string | null => {
    const hit = document.cookie.split('; ').find(c => c.startsWith(name + '='))
    if (!hit) return null
    const raw = hit.slice(name.length + 1)
    try { return decodeURIComponent(raw) || null } catch { return raw || null }
}

const store = {
    read(): string | null {
        if (SG_STORAGE_MODE === 'cookie') return readCookie(SG_STORE_KEY)
        try { return localStorage.getItem(SG_STORE_KEY) } catch { return null }
    },
    write(value: string): void {
        if (SG_STORAGE_MODE === 'cookie') {
            const maxAge = Math.floor(SG_TOKEN_TTL_MS / 1000)
            const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : ''
            document.cookie = `${SG_STORE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`
            return
        }
        try { localStorage.setItem(SG_STORE_KEY, value) } catch { /* quota / private mode */ }
    },
}

// Return the stored attribution only when it still holds a token within the
// 30-day TTL. The capturedAt check is what enforces the TTL in localStorage
// mode; in cookie mode the cookie is already gone, so it is a harmless
// belt-and-braces guard. Consumed here for first-touch and later by the
// application-submit hand-off.
export const readStargateAttribution = (): SgAttribution | null => {
    const raw = store.read()
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw) as SgAttribution
        if (!parsed || !parsed.token) return null
        if (parsed.capturedAt && Date.now() - new Date(parsed.capturedAt).getTime() > SG_TOKEN_TTL_MS) return null
        return parsed
    } catch { return null }
}

// Capture on landing. Token: sg_click query param first, sg_click cookie only
// as a fallback. First-touch: do not overwrite a still-fresh stored token, and
// only store when a token is present. The token is opaque and stored verbatim
// (URLSearchParams decodes exactly once, recovering what Stargate signed);
// never trimmed, truncated or re-encoded. Best-effort; never throws into boot.
export const captureStargateAttribution = (): void => {
    try {
        const params = new URL(window.location.href).searchParams
        const token = params.get('sg_click') || readCookie('sg_click')
        if (!token) return
        if (readStargateAttribution()) return

        const subIds: Record<string, string> = {}
        ;(['sub1', 'sub2', 'sub3', 'sub4', 'sub5'] as const).forEach(s => {
            const v = params.get(s)
            if (v) subIds[s] = v
        })
        const utm: Record<string, string> = {}
        ;(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const).forEach(u => {
            const v = params.get(u)
            if (v) utm[u] = v
        })
        const ref = params.get('ref')

        const bundle: SgAttribution = {
            token,
            ...(ref ? { ref } : {}),
            ...(Object.keys(subIds).length ? { subIds } : {}),
            ...(Object.keys(utm).length ? { utm } : {}),
            capturedAt: new Date().toISOString(),
        }
        store.write(JSON.stringify(bundle))
    } catch { /* attribution capture is best-effort; never break boot */ }
}
